"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface ProjectStageProps {
  id: string;
  title: string;
  iconId: string;
  description?: string;
  bulletPoints?: string[];
  isFirst?: boolean;
  isLast?: boolean;
}

const getIcon = (iconId: string) => {
  const icons: { [key: string]: React.ReactElement } = {
    lightbulb: (
      <svg width="24" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#clip0_3002_1449)">
          <path d="M12.5 2.81207C12.1117 2.81207 11.7969 3.12689 11.7969 3.5152C11.7969 3.90351 12.1117 4.21832 12.5 4.21832C14.8266 4.21832 16.7195 6.11085 16.7195 8.43707C16.7195 8.82539 17.0343 9.1402 17.4226 9.1402C17.8109 9.1402 18.1257 8.82539 18.1257 8.43707C18.1257 5.33545 15.602 2.81207 12.5 2.81207Z" fill="white"/>
          <path d="M10.6876 0.188902C7.51086 0.852698 4.93508 3.40382 4.25731 6.60028C3.68502 9.29943 4.39025 12.0093 6.19217 14.035C7.073 15.0251 7.57812 16.3321 7.57812 17.6245V19.0308C7.57812 19.9664 8.19064 20.7613 9.03561 21.0366C9.31348 22.6784 10.7385 23.9995 12.5 23.9995C14.261 23.9995 15.6864 22.6788 15.9644 21.0366C16.8094 20.7613 17.4219 19.9664 17.4219 19.0307V17.6245C17.4219 16.3296 17.9287 15.0382 18.8488 13.9884C20.1955 12.4526 20.9378 10.4796 20.9375 8.43703C20.9375 3.11367 16.0649 -0.934505 10.6876 0.188902ZM12.5 22.5933C11.5962 22.5933 10.8037 21.9713 10.511 21.1355H14.4889C14.1963 21.9713 13.4038 22.5933 12.5 22.5933ZM16.0156 19.0308C16.0156 19.4185 15.7002 19.7339 15.3125 19.7339H9.6875C9.2998 19.7339 8.98438 19.4185 8.98438 19.0308V18.3277H16.0156V19.0308ZM17.7913 13.0616C16.8107 14.1803 16.2074 15.5294 16.0543 16.9214H8.94584C8.79294 15.5287 8.19116 14.1663 7.24297 13.1003C5.74119 11.4121 5.15436 9.1492 5.633 6.89198C6.1917 4.25695 8.33858 2.1164 10.9753 1.56548C15.4764 0.624839 19.5312 4.00509 19.5312 8.43703C19.5315 10.1386 18.9131 11.7822 17.7913 13.0616Z" fill="white"/>
        </g>
        <defs>
          <clipPath id="clip0_3002_1449">
            <rect width="24" height="24" fill="white" transform="translate(0.5)"/>
          </clipPath>
        </defs>
      </svg>
    ),
    "clipboard-list": (
      <svg width="24" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#clip0_3002_1962)">
          <path d="M23.0938 15.5904V2.10938C23.0938 0.946266 22.1475 0 20.9844 0H5.46875C4.30564 0 3.35938 0.946266 3.35938 2.10938V2.85938H2.60938C1.44627 2.85938 0.5 3.80564 0.5 4.96875C0.5 6.13186 1.44627 7.07812 2.60938 7.07812H3.35938V9.89062H2.60938C1.44627 9.89062 0.5 10.8369 0.5 12C0.5 13.1631 1.44627 14.1094 2.60938 14.1094H3.35938V16.9219H2.60938C1.44627 16.9219 0.5 17.8681 0.5 19.0312C0.5 20.1944 1.44627 21.1406 2.60938 21.1406H3.35938V21.8438C3.35938 22.4041 3.57486 22.9381 3.96613 23.3475C4.36841 23.7683 4.90208 24 5.46875 24H19.5781C22.292 24 24.5 21.771 24.5 19.0312C24.5 17.6933 23.9631 16.4785 23.0938 15.5904Z" fill="white"/>
          <path d="M12.9956 4.47156C12.7211 4.19697 12.2759 4.19697 12.0013 4.47161L9.68598 6.78691L8.57921 5.68014C8.30466 5.40555 7.85945 5.40555 7.58485 5.68014C7.31026 5.95474 7.31026 6.39991 7.58485 6.6745L9.18882 8.27847C9.32607 8.41577 9.50602 8.48439 9.68598 8.48439C9.86593 8.48439 10.0459 8.41577 10.1831 8.27842L12.9956 5.46592C13.2702 5.19133 13.2702 4.74616 12.9956 4.47156Z" fill="white"/>
        </g>
        <defs>
          <clipPath id="clip0_3002_1962">
            <rect width="24" height="24" fill="white" transform="translate(0.5)"/>
          </clipPath>
        </defs>
      </svg>
    ),
    design: (
      <svg width="24" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M13.7442 18.3008H4.12172C3.73794 18.3004 3.36999 18.1478 3.09862 17.8764C2.82725 17.605 2.67462 17.2371 2.67422 16.8533V7.68006H20.8467V12.8858C20.8467 13.0648 20.9178 13.2365 21.0444 13.3631C21.171 13.4897 21.3427 13.5608 21.5217 13.5608C21.7007 13.5608 21.8724 13.4897 21.999 13.3631C22.1256 13.2365 22.1967 13.0648 22.1967 12.8858V4.36581C22.1959 3.6261 21.9017 2.91691 21.3787 2.39385C20.8556 1.8708 20.1464 1.5766 19.4067 1.57581H4.11422C3.37451 1.5766 2.66532 1.8708 2.14227 2.39385C1.61921 2.91691 1.32501 3.6261 1.32422 4.36581V16.8533C1.32501 17.595 1.62 18.3061 2.14446 18.8306C2.66893 19.355 3.38002 19.65 4.12172 19.6508H13.7442C13.9232 19.6508 14.0949 19.5797 14.2215 19.4531C14.3481 19.3265 14.4192 19.1548 14.4192 18.9758C14.4192 18.7968 14.3481 18.6251 14.2215 18.4985C14.0949 18.3719 13.9232 18.3008 13.7442 18.3008Z" fill="white"/>
        <path d="M22.7193 15.2865L16.2528 13.3192C16.0191 13.2484 15.7705 13.2422 15.5336 13.3014C15.2967 13.3605 15.0802 13.4828 14.9073 13.6552C14.7341 13.8278 14.6113 14.0444 14.5519 14.2816C14.4926 14.5188 14.499 14.7676 14.5705 15.0015L16.5378 21.4687C16.618 21.7436 16.7851 21.9851 17.0139 22.1571C17.2428 22.3292 17.5212 22.4226 17.8075 22.4235H17.8285C18.1113 22.4264 18.3877 22.3389 18.6173 22.1738C18.8469 22.0086 19.0178 21.7745 19.105 21.5055L20.0245 18.7732L22.7583 17.8522C23.0306 17.7638 23.2671 17.5897 23.4325 17.3559C23.5978 17.122 23.683 16.841 23.6755 16.5547C23.6744 16.2684 23.5807 15.9902 23.4083 15.7616C23.2359 15.533 22.9942 15.3663 22.7193 15.2865Z" fill="white"/>
      </svg>
    ),
    document: (
      <svg width="24" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M23.7455 2.79389L21.551 0.599391C21.4043 0.452578 21.217 0.375 21.0095 0.375H9.02745C8.60511 0.375 8.26156 0.718594 8.26156 1.14084V9.94852C8.1316 9.94104 8.00146 9.93724 7.87128 9.93713C4.09752 9.93713 1.02734 13.0073 1.02734 16.781C1.02734 20.5548 4.09752 23.625 7.87128 23.625C10.2695 23.625 12.3832 22.3848 13.6057 20.5123H23.204C23.6263 20.5123 23.9698 20.1687 23.9698 19.7464V3.33539C23.9698 3.12787 23.8923 2.94061 23.7455 2.79389Z" fill="white"/>
        <path d="M21.6643 11.7381C21.6643 11.8376 21.6248 11.9329 21.5545 12.0033C21.4841 12.0736 21.3888 12.1131 21.2893 12.1131H14.1384C14.039 12.1131 13.9436 12.0736 13.8733 12.0033C13.8029 11.9329 13.7634 11.8376 13.7634 11.7381C13.7634 11.6387 13.8029 11.5433 13.8733 11.4729C13.9436 11.4026 14.039 11.3631 14.1384 11.3631H21.2893C21.3888 11.3631 21.4841 11.4026 21.5545 11.4729C21.6248 11.5433 21.6643 11.6387 21.6643 11.7381Z" fill="white"/>
      </svg>
    ),
    users: (
      <svg width="24" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.1664 18.8802C21.9066 17.3206 20.8131 16.0408 19.3127 15.5407L15.9821 14.4305V13.9354H15.2079V14.6616L14.63 16.973L13.0665 15.0967H11.9296L10.3661 16.973L9.78856 14.6616V13.9354H9.01437V14.4305L5.68379 15.5407C4.18379 16.0408 3.09024 17.3206 2.83011 18.8802L1.97656 23.9999H23.0199L22.1664 18.8802Z" fill="white"/>
        <path d="M6.36002 7.30065C6.08828 7.63394 5.91835 8.05355 5.91835 8.51613C5.91835 9.50942 6.67319 10.3208 7.63783 10.4299C7.83525 11.1093 8.1999 11.7321 8.71396 12.2462L10.2236 13.7559C10.5891 14.1213 11.0749 14.3226 11.592 14.3226H13.4063C13.9231 14.3226 14.4093 14.1213 14.7747 13.7559L16.2844 12.2462C16.7985 11.7321 17.1631 11.1089 17.3605 10.4299C18.3248 10.3208 19.0796 9.50942 19.0796 8.51613C19.0796 8.05355 18.9097 7.63394 18.638 7.30065C19.1156 7.15665 19.4667 6.71768 19.4667 6.19355C19.4667 5.55329 18.9457 5.03226 18.3054 5.03226H18.2505C17.942 2.74723 16.2743 0.835355 14.0474 0.217935V0H10.9506V0.217935C8.72364 0.835355 7.05564 2.74761 6.74751 5.03226H6.69254C6.05228 5.03226 5.53125 5.55329 5.53125 6.19355C5.53125 6.71768 5.88235 7.15665 6.36002 7.30065Z" fill="white"/>
      </svg>
    ),
    handshake: (
      <svg width="24" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#clip0_3002_1053)">
          <path d="M23.7455 2.79389L21.551 0.599391C21.4043 0.452578 21.217 0.375 21.0095 0.375H9.02745C8.60511 0.375 8.26156 0.718594 8.26156 1.14084V9.94852C8.1316 9.94104 8.00146 9.93724 7.87128 9.93713C4.09752 9.93713 1.02734 13.0073 1.02734 16.781C1.02734 20.5548 4.09752 23.625 7.87128 23.625C10.2695 23.625 12.3832 22.3848 13.6057 20.5123H23.204C23.6263 20.5123 23.9698 20.1687 23.9698 19.7464V3.33539C23.9698 3.12787 23.8923 2.94061 23.7455 2.79389Z" fill="white"/>
        </g>
        <defs>
          <clipPath id="clip0_3002_1053">
            <rect width="24" height="24" fill="white" transform="translate(0.5)"/>
          </clipPath>
        </defs>
      </svg>
    )
  };

  return icons[iconId] || icons.lightbulb;
};

export default function ProjectStage({ 
  id, 
  title, 
  iconId, 
  description, 
  bulletPoints,
  isFirst = false, 
  isLast = false 
}: ProjectStageProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="relative">
      <motion.div 
        className="flex items-start gap-4 lg:gap-8 w-full"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Timeline Line */}
        <div className="flex flex-col items-center flex-shrink-0 relative">
          {/* Circle for non-last items, Arrow SVG for last item */}
          {!isLast ? (
            <div className="w-3 h-3 bg-[#1a6b62] border-2 border-white rounded-full z-10 relative mt-8"></div>
          ) : (
            <div className="mt-8 relative z-10">
              <svg width="13" height="16" viewBox="0 0 13 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="6.5" cy="6.5" r="5.6" fill="#1A6B62" stroke="white" strokeWidth="0.8"/>
                <path d="M6.49997 15.5L12.2735 5.5L0.726472 5.5L6.49997 15.5Z" fill="#1A6B62"/>
                <path d="M6.5 1.5L5.5 1.5L5.49997 6.5L6.49997 6.5L7.49997 6.5L7.5 1.5L6.5 1.5Z" fill="#1A6B62"/>
              </svg>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 mb-6">
          <motion.div 
            className={`${isExpanded ? 'bg-white shadow-[0_30px_60px_0_rgba(0,0,0,0.08)]' : 'bg-[#1a6b62] hover:bg-[#1a5852]'} rounded-2xl p-4 sm:p-6 lg:p-8 cursor-pointer transition-all duration-300`}
            onClick={() => setIsExpanded(!isExpanded)}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                <div className={`flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 ${isExpanded ? '[&_svg_path]:fill-[#1a6b62]' : ''}`}>
                  {getIcon(iconId)}
                </div>
                <h3 className={`${isExpanded ? 'text-[#1a6b62]' : 'text-white'} text-lg sm:text-xl lg:text-2xl font-medium leading-tight lg:leading-[34px] tracking-[-0.72px] truncate`}>
                  {title}
                </h3>
              </div>
              
              <motion.div
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.3 }}
                className={`flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 border ${isExpanded ? 'border-[#1a6b62]' : 'border-[#c9cdd3]'} rounded-full flex-shrink-0 ml-2`}
              >
                <svg width="10" height="6" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-2.5 h-1.5 sm:w-3 sm:h-2">
                  <path d="M1.793 1.6533L6.4996 6.3467L11.2063 1.6533" stroke={isExpanded ? "#1a6b62" : "#C9CDD3"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </motion.div>
            </div>

            {/* Expanded Content */}
            <AnimatePresence>
              {isExpanded && (description || bulletPoints) && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden mt-4"
                >
                  {description && (
                    <div className="text-[#3d434c] text-sm sm:text-base leading-6 sm:leading-7 text-justify mb-4">
                      {description.split('\n').map((line, index) => (
                        <p key={index} className={index > 0 ? 'mt-2' : ''}>
                          {line}
                        </p>
                      ))}
                    </div>
                  )}
                  {bulletPoints && (
                    <ul className="space-y-2">
                      {bulletPoints.map((point, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-[#1a6b62] mr-3 mt-1 text-sm">▶</span>
                          <span className="text-[#3d434c] text-sm sm:text-base leading-6">{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </motion.div>
      
      {/* Connecting Line - positioned absolutely to connect circles */}
      {!isLast && (
        <div className="absolute left-[5px] sm:left-[6px] top-11 w-0.5 h-full bg-[#1a6b62] z-0"></div>
      )}
    </div>
  );
}
